import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination";
import UserGroupInvitationBanner from "@/components/auth/UserGroupInvitationBanner";
import UserCollectionInvitationBanner from "@/components/auth/UserCollectionInvitationBanner";
import GroupSection from "@/components/group/GroupSection";
import { Separator } from "@/components/ui/separator";
import { useDashboardData } from "@/hooks/dashboard";
import { useSectionTitle } from "@/context/SectionTitleContext";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DashboardSkeleton from "./DashboardSkeleton";
import { createCollection, patchCollection } from "@/services/collection";
import { createGroup } from "@/services/groups";
import { selectUser } from "@/store/userSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SelectedHotel } from "@/components/form/SelectHotelInput";
import getLink, { LINK_TYPE } from "@/lib/links";
import { useModal } from "@/context/ModalContext";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/context/AuthContext";
import { fireTagManagerEvent } from "@/services/tagmanager";

export default function Dashboard() {
  const { t } = useTranslation();

  // SET PAGE TITLE
  const { setTitle } = useSectionTitle();
  useEffect(() => {
    setTitle("");
  }, [setTitle]);

  const {
    groups,
    collectionsGroupedByGroupId,
    groupInvitations,
    collectionInvitations,
    isLoading,
    page,
    totalPages,
    setPage,
    refresh,
  } = useDashboardData();

  const dataLoadedRef = useRef(false);

  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const someInvitations =
    groupInvitations.length > 0 || collectionInvitations.length > 0;

  const { showModal, hideModal } = useModal();
  const { logout } = useAuthContext();

  const handleCreateGroup = async () => {
    let hotelName = "",
      hotelId = "";

    //console.log("creazione gruppo/collezione");

    if (SelectedHotel.value.dest_name === "") {
      const storedHotel =
        typeof window !== "undefined"
          ? sessionStorage.getItem("selectedHotel")
          : null;
      if (storedHotel) {
        const parsedData = JSON.parse(storedHotel);
        hotelName = parsedData.dest_name;
        hotelId = parsedData.dest_id;
      } else {
        //user has no collection associated so its a wrong registration email or is a b2c user so has to be log out
        logout();
        showModal(
          <div className="flex w-full justify-end">
            <Button
              variant="black"
              onClick={() => {
                hideModal();
              }}
            >
              {t("buttons.close")}
            </Button>
          </div>,
          {
            title: t("errors.warning"),
            description: t("errors.invalid_user"),
          },
        );
        return;
      }
    } else {
      hotelName = SelectedHotel.value.dest_name;
      hotelId = SelectedHotel.value.dest_id;
    }

    try {
      const group = await createGroup({ name: hotelName });

      const collection = await createCollection({
        groupId: group?._id,
        name: hotelName,
        type: "hotel",
      });

      await patchCollection({
        collectionId: collection?._id,
        name: hotelName,
        preData: {
          bookingComId: hotelId,
        },
      });

      sessionStorage.removeItem("selectedHotel");

      if (!user?.firstName || !user?.secondName || !user?.phone) {
        navigate(getLink(LINK_TYPE.REGISTER_USER_DETAILS));
      } else {
        navigate(`/collection/${collection._id}/taks`);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const sessionStartRef = useRef(false);

  useEffect(() => {
    if (!sessionStartRef.current) {
      sessionStartRef.current = true;
      fireTagManagerEvent("session_start_dashboard");
    }
  }, []);

  useEffect(() => {
    if (!isLoading && groups && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      //console.log("Gruppi: ", groups.length);

      if (groups.length === 0 && someInvitations === false) {
        console.log("creating group");
        handleCreateGroup();
      }
      if (groups.length === 1) {
        const collections = collectionsGroupedByGroupId[groups[0]._id];

        if (collections?.length === 1) {
          if (!user?.firstName || !user?.secondName || !user?.phone) {
            navigate(getLink(LINK_TYPE.REGISTER_USER_DETAILS));
          } else {
            navigate(`/collection/${collections[0]._id}/taks`);
          }
        }
      }
    }
  }, [isLoading, groups]);

  if (isLoading) return <DashboardSkeleton />;

  return (
    <>
      <div id="dashboard">
        <div className="space-y-8">
          {someInvitations && (
            <>
              <div className="space-y-4">
                {groupInvitations.map((group, index) => (
                  <UserGroupInvitationBanner
                    onRefresh={() => refresh()}
                    key={"groupInvitation" + index}
                    group={group}
                  />
                ))}

                {collectionInvitations.map((collection, index) => (
                  <UserCollectionInvitationBanner
                    onRefresh={() => refresh()}
                    key={"collectionInvitation" + index}
                    collection={collection}
                  />
                ))}
              </div>
              {groupInvitations.length > 0 && <Separator className="my-10" />}
            </>
          )}
          {groups.length > 0 && (
            <div className="space-y-20">
              {groups.map((group) => (
                <GroupSection
                  onRefresh={() => refresh()}
                  group={group}
                  collections={collectionsGroupedByGroupId[group._id]}
                  key={group._id}
                />
              ))}
            </div>
          )}
        </div>

        {groups.length > 5 ? (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={() => setPage(Math.max(page - 1, 1))}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    isActive={page === i + 1}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={() => setPage(Math.min(page + 1, totalPages))}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        ) : null}
      </div>
    </>
  );
}
